import {useState, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Save,
  Loader2,
  AlertCircle,
  Camera,
  Building2,
  MapPin,
  Mail,
  Phone,
  FileText,
  Landmark,
  Layers,
  Eye,
  EyeOff,
  Download,
  ChevronRight,
  Pencil,
  FolderOpen,
  GraduationCap,
  Bot,
  HelpCircle,
  Ticket,
  LayoutDashboard,
  type LucideIcon,
} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button, buttonVariants} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {Textarea} from '@common-web/components/ui/textarea';
import {Avatar, AvatarFallback, AvatarImage} from '@common-web/components/ui/avatar';
import {Badge} from '@common-web/components/ui/badge';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {trpc} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {useClientFeatures} from '@common-web/hooks/useClientFeatures';
import {formatFileSize} from '@common-web/lib/resourceUtils';
import {
  resolveBrokerCommissionAmountEnabled,
  resolveMenuItemEnabled,
  type MenuKey,
} from '@common/menuFlags';
import {StateSelect} from '@client/components/StateSelect';
import {StateMultiSelect} from '@client/components/StateMultiSelect';
import type {UsStateCode} from '@common/constants/usStates';
import {HeroBand} from '@client/components/broker-ui/hero-band';
import {cn} from '@common-web/lib/utils';

// Segmented-pill tab styling, matching the demo tab bar introduced in the
// BrokerCommissions redesign (Task 6).
const pillTabTriggerClassName = cn(
  'rounded-[9px] px-4 py-2 text-[13px] font-medium text-[var(--nbui-ink-500)]',
  'data-[state=active]:bg-white data-[state=active]:text-[var(--nbui-ink-900)]',
  'data-[state=active]:font-semibold data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,0.10)]',
);

const maskValue = (value: string | null | undefined): string => {
  if (!value) return '';
  if (value.length <= 4) return '****';
  return '****' + value.slice(-4);
};

// Quick links mirror the broker sidebar nav (app-sidebar.tsx) so visibility
// tracks the same per-tenant menu flags. `broker.profile` is omitted (this is
// that page) and `broker.dashboard` is the portal home, surfaced separately.
type QuickLink = {title: string; url: string; icon: LucideIcon; menuKey: MenuKey};

const QUICK_LINKS: QuickLink[] = [
  {title: 'My Groups', url: '/broker/groups', icon: Building2, menuKey: 'broker.groups'},
  {title: 'Accounts', url: '/accounts', icon: LayoutDashboard, menuKey: 'broker.accounts'},
  {title: 'Pipeline', url: '/broker/deals', icon: FileText, menuKey: 'broker.pipeline'},
  {title: 'Messages', url: '/broker/messages', icon: Mail, menuKey: 'broker.messages'},
  {title: 'Documents', url: '/broker/documents', icon: FileText, menuKey: 'broker.documents'},
  {title: 'Resources', url: '/broker/resources', icon: FolderOpen, menuKey: 'broker.resources'},
  {title: 'Contracts', url: '/broker/contracts', icon: FileText, menuKey: 'broker.contracts'},
  {title: 'Training', url: '/broker/training', icon: GraduationCap, menuKey: 'broker.training'},
  {
    title: 'Pitch Training',
    url: '/broker/pitch-training',
    icon: Bot,
    menuKey: 'broker.pitchTraining',
  },
  {title: 'Help & Support', url: '/broker/support', icon: HelpCircle, menuKey: 'broker.support'},
  {title: 'Tickets', url: '/broker/tickets', icon: Ticket, menuKey: 'broker.tickets'},
];

export const BrokerProfileRedesign = () => {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const features = useClientFeatures();
  const {data: profile, isLoading, error} = trpc.broker.getProfile.useQuery();
  const {toast} = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showRoutingNumber, setShowRoutingNumber] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [showTaxId, setShowTaxId] = useState(false);
  const [formData, setFormData] = useState({
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    taxId: '',
    npn: '',
    licensedStates: [] as UsStateCode[],
    title: '',
    bio: '',
    location: '',
  });

  const [isEditingBanking, setIsEditingBanking] = useState(false);
  const [isSavingBanking, setIsSavingBanking] = useState(false);
  const [bankingForm, setBankingForm] = useState({
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: '' as '' | 'CHECKING' | 'SAVINGS',
  });

  const bankingQuery = trpc.broker.getBankingInfo.useQuery();
  const updateBankingMutation = trpc.broker.updateBankingInfo.useMutation({
    onSuccess: () => {
      toast({
        title: 'Banking updated',
        description: 'Your banking information has been successfully updated.',
        variant: 'default',
      });
      bankingQuery.refetch();
      setIsEditingBanking(false);
    },
    onError: (err) => {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update banking information',
        variant: 'destructive',
      });
    },
  });

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const updateProfileMutation = trpc.broker.updateProfile.useMutation({
    onSuccess: () => {
      toast({
        title: 'Profile updated',
        description: 'Your profile has been successfully updated.',
        variant: 'default',
      });
      utils.broker.getProfile.invalidate();
      setIsEditing(false);
    },
    onError: (err) => {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update profile',
        variant: 'destructive',
      });
    },
  });

  const getAvatarUploadUrlMutation = trpc.broker.getAvatarUploadUrl.useMutation();

  // Separate mutation for avatar uploads: unlike a full profile save, it must not
  // close the edit form (which would discard unsaved profile edits).
  const updateAvatarMutation = trpc.broker.updateProfile.useMutation({
    onSuccess: () => {
      utils.broker.getProfile.invalidate();
    },
  });

  const handleAvatarUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file',
        variant: 'destructive',
      });
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const {uploadUrl, key} = await getAvatarUploadUrlMutation.mutateAsync({
        fileName: file.name,
        contentType: file.type,
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {'Content-Type': file.type},
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload avatar');
      }

      await updateAvatarMutation.mutateAsync({avatar: key});

      toast({
        title: 'Avatar updated',
        description: 'Your profile photo has been updated.',
        variant: 'default',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to upload avatar',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleStartEdit = () => {
    if (profile) {
      setFormData({
        contactFirstName: profile.contactFirstName || '',
        contactLastName: profile.contactLastName || '',
        contactEmail: profile.contactEmail || '',
        contactPhone: profile.contactPhone || '',
        addressLine1: profile.addressLine1 || '',
        addressLine2: profile.addressLine2 || '',
        city: profile.city || '',
        state: profile.state || '',
        zipCode: profile.zipCode || '',
        taxId: profile.taxId || '',
        npn: profile.npn || '',
        licensedStates: (profile.licensedStates || []) as UsStateCode[],
        title: profile.title || '',
        bio: profile.bio || '',
        location: profile.location || '',
      });
    }
    setIsEditing(true);
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await updateProfileMutation.mutateAsync(formData);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEditBanking = () => {
    if (!bankingQuery.isSuccess) return;

    const bankInfo = bankingQuery.data?.bankInfo;
    setBankingForm({
      bankName: bankInfo?.bankName || '',
      routingNumber: bankInfo?.routingNumber || '',
      accountNumber: bankInfo?.accountNumber || '',
      accountType: (bankInfo?.accountType as '' | 'CHECKING' | 'SAVINGS') || '',
    });
    setIsEditingBanking(true);
    setShowRoutingNumber(false);
    setShowAccountNumber(false);
  };

  const handleSaveBanking = async () => {
    if (!bankingForm.accountType) {
      toast({
        title: 'Validation Error',
        description: 'Please select an account type',
        variant: 'destructive',
      });
      return;
    }
    setIsSavingBanking(true);
    try {
      await updateBankingMutation.mutateAsync({
        bankName: bankingForm.bankName,
        routingNumber: bankingForm.routingNumber,
        accountNumber: bankingForm.accountNumber,
        accountType: bankingForm.accountType as 'CHECKING' | 'SAVINGS',
      });
    } finally {
      setIsSavingBanking(false);
    }
  };

  const quickLinks = QUICK_LINKS.filter((link) => resolveMenuItemEnabled(link.menuKey, features));
  const groupsEnabled = resolveMenuItemEnabled('broker.groups', features);
  const documentsEnabled = resolveMenuItemEnabled('broker.documents', features);
  const commissionAmountEnabled = resolveBrokerCommissionAmountEnabled(features);
  const documents = profile?.documents ?? [];

  if (error) {
    return (
      <div className='px-8 py-6 max-w-6xl mx-auto'>
        <Alert variant='destructive' className='border-red-200 bg-red-50'>
          <AlertCircle className='h-4 w-4' />
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const initials =
    profile?.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'BP';

  return (
    <div className='px-8 py-6'>
      <HeroBand
        title='My Profile'
        subtitle='Manage your profile details, banking, and documents.'
      />

      <div className='max-w-6xl mx-auto w-full'>
        <div className='flex flex-col lg:flex-row gap-6 items-start'>
          {/* Left identity + navigation sidebar */}
          <div className='w-full lg:w-80 shrink-0 space-y-6 lg:sticky lg:top-6'>
            <Card className='border-0 shadow-lg bg-white overflow-hidden'>
              <CardContent className='p-6 flex flex-col items-center text-center'>
                <div className='relative group'>
                  {isLoading ? (
                    <Skeleton className='h-24 w-24 rounded-full' />
                  ) : (
                    <>
                      <Avatar className='h-24 w-24 border-4 border-muted'>
                        <AvatarImage src={profile?.avatarUrl || undefined} alt={profile?.name} />
                        <AvatarFallback className='bg-brand-blue text-white text-2xl'>
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <input
                        ref={avatarInputRef}
                        type='file'
                        accept='image/*'
                        className='hidden'
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleAvatarUpload(file);
                        }}
                      />
                      <button
                        type='button'
                        aria-label='Upload profile photo'
                        className='absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity cursor-pointer'
                        onClick={() => avatarInputRef.current?.click()}
                        disabled={isUploadingAvatar}
                      >
                        {isUploadingAvatar ? (
                          <Loader2 className='h-6 w-6 text-white animate-spin' />
                        ) : (
                          <Camera className='h-6 w-6 text-white' />
                        )}
                      </button>
                    </>
                  )}
                </div>

                {isLoading ? (
                  <div className='mt-4 w-full space-y-2 flex flex-col items-center'>
                    <Skeleton className='h-6 w-40' />
                    <Skeleton className='h-4 w-28' />
                  </div>
                ) : (
                  <>
                    <h2 className='mt-4 text-xl font-bold text-brand-navy'>{profile?.name}</h2>
                    {profile?.title && (
                      <p className='text-muted-foreground text-sm'>{profile.title}</p>
                    )}
                    {profile?.client?.name && (
                      <p className='text-muted-foreground text-sm'>{profile.client.name}</p>
                    )}
                    {profile?.location && (
                      <div className='mt-1 flex items-center gap-1 text-muted-foreground text-sm'>
                        <MapPin className='h-3.5 w-3.5' />
                        <span>{profile.location}</span>
                      </div>
                    )}

                    <div className='mt-4 pt-4 border-t border-gray-100 w-full space-y-2 text-left'>
                      {profile?.contactEmail && (
                        <div className='flex items-center gap-2 text-sm text-brand-navy'>
                          <Mail className='h-3.5 w-3.5 text-muted-foreground shrink-0' />
                          <span className='truncate'>{profile.contactEmail}</span>
                        </div>
                      )}
                      {profile?.contactPhone && (
                        <div className='flex items-center gap-2 text-sm text-brand-navy'>
                          <Phone className='h-3.5 w-3.5 text-muted-foreground shrink-0' />
                          <span>{profile.contactPhone}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className='border-0 shadow-lg bg-white overflow-hidden'>
              <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
                <CardTitle className='text-sm font-semibold text-brand-navy uppercase tracking-wide'>
                  Quick Stats
                </CardTitle>
              </CardHeader>
              <CardContent className='p-2'>
                {groupsEnabled && (
                  <button
                    onClick={() => navigate('/broker/groups')}
                    className='w-full flex items-center justify-between px-4 py-3 rounded-md hover:bg-gray-50 transition-colors text-left'
                  >
                    <span className='flex items-center gap-2 text-sm text-brand-navy'>
                      <Building2 className='h-4 w-4 text-muted-foreground' />
                      Groups
                    </span>
                    <span className='flex items-center gap-1 font-semibold text-brand-navy'>
                      {profile?.stats?.totalGroups ?? 0}
                      <ChevronRight className='h-4 w-4 text-muted-foreground' />
                    </span>
                  </button>
                )}
                <div className='flex items-center justify-between px-4 py-3'>
                  <span className='flex items-center gap-2 text-sm text-brand-navy'>
                    <Layers className='h-4 w-4 text-muted-foreground' />
                    Plans
                  </span>
                  <span className='font-semibold text-brand-navy'>
                    {profile?.stats?.totalPlans ?? 0}
                  </span>
                </div>
              </CardContent>
            </Card>

            {profile && commissionAmountEnabled && (
              <Card className='border-0 shadow-lg bg-white overflow-hidden'>
                <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
                  <CardTitle className='text-sm font-semibold text-brand-navy uppercase tracking-wide'>
                    Commission Amount
                  </CardTitle>
                </CardHeader>
                <CardContent className='px-6 py-4'>
                  <p className='text-sm text-brand-navy whitespace-pre-wrap break-words'>
                    {profile.commissionAmount || 'Not set'}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Quick Links — gated by the same broker menu flags as the sidebar */}
            {quickLinks.length > 0 && (
              <Card className='border-0 shadow-lg bg-white overflow-hidden'>
                <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
                  <CardTitle className='text-sm font-semibold text-brand-navy uppercase tracking-wide'>
                    Quick Links
                  </CardTitle>
                </CardHeader>
                <CardContent className='p-2'>
                  {quickLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <button
                        key={link.menuKey}
                        onClick={() => navigate(link.url)}
                        className='w-full flex items-center justify-between px-4 py-2.5 rounded-md hover:bg-gray-50 transition-colors text-left'
                      >
                        <span className='flex items-center gap-2 text-sm text-brand-navy'>
                          <Icon className='h-4 w-4 text-muted-foreground' />
                          {link.title}
                        </span>
                        <ChevronRight className='h-4 w-4 text-muted-foreground' />
                      </button>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right tabbed content */}
          <div className='flex-1 min-w-0 w-full'>
            <Tabs defaultValue='personal' className='w-full'>
              <TabsList
                className={cn(
                  'mb-4 h-auto items-center gap-1 rounded-[12px] bg-[hsl(var(--background))]',
                  'border border-[var(--nbui-border-soft)] p-1',
                )}
              >
                <TabsTrigger value='personal' className={pillTabTriggerClassName}>
                  Personal Info
                </TabsTrigger>
                <TabsTrigger value='banking' className={pillTabTriggerClassName}>
                  Banking
                </TabsTrigger>
                {documentsEnabled && (
                  <TabsTrigger value='documents' className={pillTabTriggerClassName}>
                    Documents
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Personal Info */}
              <TabsContent value='personal' className='space-y-6'>
                {/* About Me */}
                <Card className='border-0 shadow-lg bg-white overflow-hidden'>
                  <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4 flex flex-row items-center justify-between'>
                    <CardTitle className='text-lg font-semibold text-brand-navy'>
                      About Me
                    </CardTitle>
                    {!isEditing && (
                      <Button variant='outline' size='sm' onClick={handleStartEdit}>
                        <Pencil className='h-4 w-4 mr-2' />
                        Edit Profile
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className='p-6'>
                    {isLoading ? (
                      <div className='space-y-4'>
                        <Skeleton className='h-10 w-full' />
                        <Skeleton className='h-24 w-full' />
                      </div>
                    ) : isEditing ? (
                      <div className='space-y-4'>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                          <div>
                            <Label htmlFor='title'>Professional Title</Label>
                            <Input
                              id='title'
                              placeholder='e.g., Senior Benefits Consultant'
                              value={formData.title}
                              onChange={(e) => setFormData({...formData, title: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label htmlFor='location'>Location</Label>
                            <Input
                              id='location'
                              placeholder='e.g., Dallas, TX'
                              value={formData.location}
                              onChange={(e) => setFormData({...formData, location: e.target.value})}
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor='bio'>Bio</Label>
                          <Textarea
                            id='bio'
                            placeholder='Tell potential clients and colleagues about yourself...'
                            value={formData.bio}
                            onChange={(e) => setFormData({...formData, bio: e.target.value})}
                            rows={4}
                            className='resize-none'
                            maxLength={2000}
                          />
                          <p className='text-xs text-gray-500 mt-1'>
                            {formData.bio.length}/2000 characters
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className='space-y-4'>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                          <div>
                            <Label className='text-xs text-gray-500'>Professional Title</Label>
                            <p className='text-brand-navy font-medium'>
                              {profile?.title || 'Not set'}
                            </p>
                          </div>
                          <div>
                            <Label className='text-xs text-gray-500'>Location</Label>
                            <p className='text-brand-navy font-medium'>
                              {profile?.location || 'Not set'}
                            </p>
                          </div>
                        </div>
                        <div>
                          <Label className='text-xs text-gray-500'>Bio</Label>
                          <p className='text-brand-navy whitespace-pre-wrap'>
                            {profile?.bio || 'No bio added yet. Click Edit Profile to add one.'}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Contact Information */}
                <Card className='border-0 shadow-lg bg-white overflow-hidden'>
                  <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
                    <CardTitle className='text-lg font-semibold text-brand-navy'>
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='p-6'>
                    {isLoading ? (
                      <div className='space-y-4'>
                        <Skeleton className='h-10 w-full' />
                        <Skeleton className='h-10 w-full' />
                      </div>
                    ) : isEditing ? (
                      <div className='space-y-4'>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                          <div>
                            <Label htmlFor='contactFirstName'>First Name</Label>
                            <Input
                              id='contactFirstName'
                              value={formData.contactFirstName}
                              onChange={(e) =>
                                setFormData({...formData, contactFirstName: e.target.value})
                              }
                            />
                          </div>
                          <div>
                            <Label htmlFor='contactLastName'>Last Name</Label>
                            <Input
                              id='contactLastName'
                              value={formData.contactLastName}
                              onChange={(e) =>
                                setFormData({...formData, contactLastName: e.target.value})
                              }
                            />
                          </div>
                        </div>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                          <div>
                            <Label htmlFor='contactEmail'>Email</Label>
                            <Input
                              id='contactEmail'
                              type='email'
                              value={formData.contactEmail}
                              onChange={(e) =>
                                setFormData({...formData, contactEmail: e.target.value})
                              }
                            />
                          </div>
                          <div>
                            <Label htmlFor='contactPhone'>Phone</Label>
                            <Input
                              id='contactPhone'
                              value={formData.contactPhone}
                              onChange={(e) =>
                                setFormData({...formData, contactPhone: e.target.value})
                              }
                            />
                          </div>
                        </div>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                          <div>
                            <Label htmlFor='taxId'>Tax ID</Label>
                            <Input
                              id='taxId'
                              value={formData.taxId}
                              onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label htmlFor='npn'>NPN</Label>
                            <Input
                              id='npn'
                              placeholder='National Producer Number'
                              maxLength={20}
                              value={formData.npn}
                              onChange={(e) => setFormData({...formData, npn: e.target.value})}
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor='licensedStates'>Licensed States</Label>
                          <StateMultiSelect
                            id='licensedStates'
                            value={formData.licensedStates}
                            onChange={(v) => setFormData({...formData, licensedStates: v})}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        <div>
                          <Label className='text-xs text-gray-500'>First Name</Label>
                          <p className='text-brand-navy font-medium'>
                            {profile?.contactFirstName || '-'}
                          </p>
                        </div>
                        <div>
                          <Label className='text-xs text-gray-500'>Last Name</Label>
                          <p className='text-brand-navy font-medium'>
                            {profile?.contactLastName || '-'}
                          </p>
                        </div>
                        <div>
                          <Label className='text-xs text-gray-500'>Email</Label>
                          <p className='text-brand-navy font-medium'>
                            {profile?.contactEmail || '-'}
                          </p>
                        </div>
                        <div>
                          <Label className='text-xs text-gray-500'>Phone</Label>
                          <p className='text-brand-navy font-medium'>
                            {profile?.contactPhone || '-'}
                          </p>
                        </div>
                        <div>
                          <Label className='text-xs text-gray-500'>Tax ID</Label>
                          <div className='flex items-center gap-2'>
                            <p className='text-brand-navy font-medium'>
                              {profile?.taxId
                                ? showTaxId
                                  ? profile.taxId
                                  : maskValue(profile.taxId)
                                : '-'}
                            </p>
                            {profile?.taxId && (
                              <button
                                type='button'
                                aria-label={showTaxId ? 'Hide tax ID' : 'Show tax ID'}
                                className='text-gray-400 hover:text-gray-600'
                                onClick={() => setShowTaxId(!showTaxId)}
                              >
                                {showTaxId ? (
                                  <EyeOff className='h-4 w-4' />
                                ) : (
                                  <Eye className='h-4 w-4' />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                        <div>
                          <Label className='text-xs text-gray-500'>NPN</Label>
                          <p className='text-brand-navy font-medium'>{profile?.npn || '-'}</p>
                        </div>
                        <div className='sm:col-span-2'>
                          <Label className='text-xs text-gray-500'>Licensed States</Label>
                          {profile?.licensedStates?.length ? (
                            <div className='flex flex-wrap gap-1.5 mt-1'>
                              {profile.licensedStates.map((code) => (
                                <Badge key={code} variant='secondary'>
                                  {code}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <p className='text-brand-navy font-medium'>-</p>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Address */}
                <Card className='border-0 shadow-lg bg-white overflow-hidden'>
                  <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
                    <CardTitle className='text-lg font-semibold text-brand-navy'>Address</CardTitle>
                  </CardHeader>
                  <CardContent className='p-6'>
                    {isLoading ? (
                      <div className='space-y-4'>
                        <Skeleton className='h-10 w-full' />
                        <Skeleton className='h-10 w-full' />
                      </div>
                    ) : isEditing ? (
                      <div className='space-y-4'>
                        <div>
                          <Label htmlFor='addressLine1'>Address Line 1</Label>
                          <Input
                            id='addressLine1'
                            value={formData.addressLine1}
                            onChange={(e) =>
                              setFormData({...formData, addressLine1: e.target.value})
                            }
                          />
                        </div>
                        <div>
                          <Label htmlFor='addressLine2'>Address Line 2</Label>
                          <Input
                            id='addressLine2'
                            value={formData.addressLine2}
                            onChange={(e) =>
                              setFormData({...formData, addressLine2: e.target.value})
                            }
                          />
                        </div>
                        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
                          <div>
                            <Label htmlFor='city'>City</Label>
                            <Input
                              id='city'
                              value={formData.city}
                              onChange={(e) => setFormData({...formData, city: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label htmlFor='state'>State</Label>
                            <StateSelect
                              id='state'
                              value={formData.state}
                              onValueChange={(v) => setFormData({...formData, state: v})}
                            />
                          </div>
                          <div>
                            <Label htmlFor='zipCode'>Zip Code</Label>
                            <Input
                              id='zipCode'
                              value={formData.zipCode}
                              onChange={(e) => setFormData({...formData, zipCode: e.target.value})}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='space-y-2'>
                        <p className='text-brand-navy font-medium'>
                          {profile?.addressLine1 || 'No address on file'}
                        </p>
                        {profile?.addressLine2 && (
                          <p className='text-brand-navy font-medium'>{profile.addressLine2}</p>
                        )}
                        {(profile?.city || profile?.state || profile?.zipCode) && (
                          <p className='text-brand-navy font-medium'>
                            {[profile?.city, profile?.state, profile?.zipCode]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {isEditing && (
                  <div className='flex gap-4 justify-end'>
                    <Button
                      variant='outline'
                      onClick={() => setIsEditing(false)}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleSaveProfile} disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className='h-4 w-4 mr-2' />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </TabsContent>

              {/* Banking */}
              <TabsContent value='banking' className='space-y-6'>
                <Card className='border-0 shadow-lg bg-white overflow-hidden'>
                  <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4 flex flex-row items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <Landmark className='h-5 w-5 text-brand-navy' />
                      <CardTitle className='text-lg font-semibold text-brand-navy'>
                        Banking Information
                      </CardTitle>
                    </div>
                    {!isEditingBanking && profile?.vbaPayeeId && bankingQuery.isSuccess && (
                      <Button variant='outline' size='sm' onClick={handleStartEditBanking}>
                        <Pencil className='h-4 w-4 mr-2' />
                        Edit
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className='p-6'>
                    <p className='text-xs text-gray-500 mb-4'>
                      Banking information is used for commission payments
                    </p>
                    {bankingQuery.isLoading ? (
                      <div className='space-y-4'>
                        <Skeleton className='h-10 w-full' />
                        <Skeleton className='h-10 w-full' />
                      </div>
                    ) : bankingQuery.error ? (
                      <div className='space-y-3'>
                        <Alert variant='destructive'>
                          <AlertCircle className='h-4 w-4' />
                          <AlertDescription>Failed to load banking information.</AlertDescription>
                        </Alert>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => void bankingQuery.refetch()}
                          disabled={bankingQuery.isFetching}
                        >
                          {bankingQuery.isFetching ? (
                            <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                          ) : null}
                          Retry
                        </Button>
                      </div>
                    ) : !profile?.vbaPayeeId ? (
                      <p className='text-sm text-gray-500'>
                        Banking not available - contact administrator
                      </p>
                    ) : isEditingBanking ? (
                      <div className='space-y-4'>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                          <div>
                            <Label htmlFor='bankName'>Bank Name</Label>
                            <Input
                              id='bankName'
                              placeholder='e.g., Chase, Wells Fargo'
                              value={bankingForm.bankName}
                              onChange={(e) =>
                                setBankingForm({...bankingForm, bankName: e.target.value})
                              }
                            />
                          </div>
                          <div>
                            <Label htmlFor='bankAccountType'>Account Type</Label>
                            <Select
                              value={bankingForm.accountType}
                              onValueChange={(value) =>
                                setBankingForm({
                                  ...bankingForm,
                                  accountType: value as 'CHECKING' | 'SAVINGS',
                                })
                              }
                            >
                              <SelectTrigger id='bankAccountType'>
                                <SelectValue placeholder='Select account type' />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value='CHECKING'>Checking</SelectItem>
                                <SelectItem value='SAVINGS'>Savings</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                          <div>
                            <Label htmlFor='bankRoutingNumber'>Routing Number</Label>
                            <div className='relative'>
                              <Input
                                id='bankRoutingNumber'
                                type={showRoutingNumber ? 'text' : 'password'}
                                placeholder='Enter routing number'
                                value={bankingForm.routingNumber}
                                onChange={(e) =>
                                  setBankingForm({...bankingForm, routingNumber: e.target.value})
                                }
                              />
                              <button
                                type='button'
                                aria-label={
                                  showRoutingNumber ? 'Hide routing number' : 'Show routing number'
                                }
                                className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                                onClick={() => setShowRoutingNumber(!showRoutingNumber)}
                              >
                                {showRoutingNumber ? (
                                  <EyeOff className='h-4 w-4' />
                                ) : (
                                  <Eye className='h-4 w-4' />
                                )}
                              </button>
                            </div>
                          </div>
                          <div>
                            <Label htmlFor='bankAccountNumber'>Account Number</Label>
                            <div className='relative'>
                              <Input
                                id='bankAccountNumber'
                                type={showAccountNumber ? 'text' : 'password'}
                                placeholder='Enter account number'
                                value={bankingForm.accountNumber}
                                onChange={(e) =>
                                  setBankingForm({...bankingForm, accountNumber: e.target.value})
                                }
                              />
                              <button
                                type='button'
                                aria-label={
                                  showAccountNumber ? 'Hide account number' : 'Show account number'
                                }
                                className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                                onClick={() => setShowAccountNumber(!showAccountNumber)}
                              >
                                {showAccountNumber ? (
                                  <EyeOff className='h-4 w-4' />
                                ) : (
                                  <Eye className='h-4 w-4' />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className='flex gap-4 justify-end'>
                          <Button
                            variant='outline'
                            onClick={() => setIsEditingBanking(false)}
                            disabled={isSavingBanking}
                          >
                            Cancel
                          </Button>
                          <Button onClick={handleSaveBanking} disabled={isSavingBanking}>
                            {isSavingBanking ? (
                              <>
                                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className='h-4 w-4 mr-2' />
                                Save Banking
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        <div>
                          <Label className='text-xs text-gray-500'>Bank Name</Label>
                          <p className='text-brand-navy font-medium'>
                            {bankingQuery.data?.bankInfo?.bankName || '-'}
                          </p>
                        </div>
                        <div>
                          <Label className='text-xs text-gray-500'>Account Type</Label>
                          <p className='text-brand-navy font-medium'>
                            {bankingQuery.data?.bankInfo?.accountType
                              ? bankingQuery.data.bankInfo.accountType === 'CHECKING'
                                ? 'Checking'
                                : 'Savings'
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <Label className='text-xs text-gray-500'>Routing Number</Label>
                          <p className='text-brand-navy font-medium font-mono'>
                            {maskValue(bankingQuery.data?.bankInfo?.routingNumber) || '-'}
                          </p>
                        </div>
                        <div>
                          <Label className='text-xs text-gray-500'>Account Number</Label>
                          <p className='text-brand-navy font-medium font-mono'>
                            {maskValue(bankingQuery.data?.bankInfo?.accountNumber) || '-'}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Documents */}
              <TabsContent value='documents' className='space-y-6'>
                <Card className='border-0 shadow-lg bg-white overflow-hidden'>
                  <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4 flex flex-row items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <FileText className='h-5 w-5 text-brand-navy' />
                      <CardTitle className='text-lg font-semibold text-brand-navy'>
                        Documents
                      </CardTitle>
                    </div>
                    {documentsEnabled && (
                      <Button size='sm' onClick={() => navigate('/broker/documents')}>
                        <FileText className='h-4 w-4 mr-2' />
                        Manage Documents
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className='p-6'>
                    {isLoading ? (
                      <div className='space-y-3'>
                        <Skeleton className='h-14 w-full' />
                        <Skeleton className='h-14 w-full' />
                      </div>
                    ) : documents.length === 0 ? (
                      <p className='text-gray-500 text-sm'>
                        No documents yet. Use "Manage Documents" to upload your licenses, contracts,
                        and other important files.
                      </p>
                    ) : (
                      <div className='space-y-3'>
                        {documents.map((doc) => (
                          <div
                            key={doc.id}
                            className='flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors'
                          >
                            <div className='h-10 w-10 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0'>
                              <FileText className='h-5 w-5 text-brand-blue' />
                            </div>
                            <div className='flex-1 min-w-0'>
                              <p className='text-sm font-medium text-brand-navy truncate'>
                                {doc.title}
                              </p>
                              <div className='flex items-center gap-2 text-xs text-gray-500'>
                                {doc.fileType && (
                                  <Badge variant='outline' className='uppercase'>
                                    {doc.fileType}
                                  </Badge>
                                )}
                                {typeof doc.fileSize === 'number' && (
                                  <span>{formatFileSize(doc.fileSize)}</span>
                                )}
                                <span>
                                  {new Date(doc.createdAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })}
                                </span>
                              </div>
                            </div>
                            {doc.downloadUrl && (
                              <a
                                href={doc.downloadUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={cn(
                                  buttonVariants({variant: 'outline', size: 'sm'}),
                                  'shrink-0',
                                )}
                              >
                                <Download className='h-4 w-4 mr-2' />
                                Download
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};
